import { Inject, Injectable, ViewContainerRef } from "@angular/core";
import { Graph } from "@maxgraph/core";
import { DwdmDiagramAbstractConverterLinks } from "../../diagram/converter/dwdm-diagram-abstract-converter-links";
import {
    EndpointElementIdentifier,
    IntermediateElementIdentifier,
    LinksConverterData
} from "../../diagram/converter/dwdm-diagram-converter.models";
import {
    CustomCell,
    LinkElement
} from "../../diagram/models/diagram.models";
import { PopoverDataService } from "../../diagram/popover/popover-data.service";
import { PopoverVisibilityManagerService } from "../../diagram/popover/popover-visibility-manager.service";
import { DwdmCircuitDiagnosticData, DwdmLinkDiagnosticData } from "../../dwdm-circuit.model";
import { LinkInfoCircuit } from "../../wizard/steps/4-links/links-step.model";
import { DiagramCardData, DiagramCardInformation } from "@nms-angular-toolkit/nms-diagram-card";
import { DECIBEL, KILOMETER } from "@nms-ng2/app/shared/constants/units";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { PopoverHelperService } from "../../diagram/popover/popover-helper.service";
import { LINK_SITUATION } from "@nms-ng2/app/modules/topology/links/links.model";

/**
 * Responsável por converter @see LinksConverterData em @see LinkElement.
 */
@Injectable({
    providedIn: "root"
})
export class DwdmDiagramDiagnosticConverterLinksService extends DwdmDiagramAbstractConverterLinks {

    private readonly severityLabelBySeverity;
    private readonly translatedDiagramCardField;

    constructor(private readonly buildElementPopoverService: PopoverDataService,
        private readonly popoverVisibilityManagerService: PopoverVisibilityManagerService,
        @Inject(ANGULARJS_TRANSLATE) private readonly translate,
        private readonly popoverHelperService: PopoverHelperService,) {
        super();
        this.severityLabelBySeverity = {
            NORMAL: translate.instant("status.normal"),
            UNKNOWN: translate.instant("status.unknown"),
            MAJOR_FAULT: translate.instant("status.majorFault"),
            MINOR_FAULT: translate.instant("status.minorFault"),
            CRITICAL_FAULT: translate.instant("status.criticalFault"),
            DISABLED: translate.instant("status.disabled")
        };
        this.translatedDiagramCardField = {
            ORIGIN_A_END: translate.instant("dwdm.circuit.modal.diagnostic.table.aEnd"),
            DESTINATION_Z_END: translate.instant("dwdm.circuit.modal.diagnostic.table.zEnd"),
            DISTANCE: translate.instant("dwdm.circuit.modal.diagnostic.table.distance"),
            TOTAL_ATTENUATION: translate.instant("dwdm.circuit.modal.diagnostic.table.total.attenuation"),
            SITUATION: translate.instant("dwdm.circuit.modal.diagnostic.table.situation")
        };
    }

    public convert(linksData: LinksConverterData, aEndIdentifier: EndpointElementIdentifier,
        zEndIdentifier: EndpointElementIdentifier,intermediatesIdentifier: IntermediateElementIdentifier[],
        dwdmCircuitDiagnosticData: DwdmCircuitDiagnosticData, diagnosticModalViewRef: ViewContainerRef): LinkElement[] {

        return super.convertData(linksData, aEndIdentifier, zEndIdentifier, intermediatesIdentifier,
            (linkElement: LinkElement, link: LinkInfoCircuit) => {
                linkElement.popoverData = this.createPopoverData(dwdmCircuitDiagnosticData, link);
                linkElement.buildPopover = (graph: Graph, cell: CustomCell) => {
                    const target = this.buildElementPopoverService.create(graph, cell, cell.popoverData);

                    this.popoverVisibilityManagerService.show(diagnosticModalViewRef, target)
                }
            });
    }

    private createPopoverData(dwdmCircuitDiagnosticData: DwdmCircuitDiagnosticData, link: LinkInfoCircuit): DiagramCardData {
        const data = dwdmCircuitDiagnosticData.links.find(linkDiagnostic => {
            return linkDiagnostic.name === link.name;
        });

        const cardData = new DiagramCardData();
        cardData.title = data.name;
        cardData.informationBlocks = this.buildInformationBlocks(data);
        cardData.status = this.buildStatus(data);

        return cardData;
    }

    protected shouldCreateDefaultLink(linksData: LinksConverterData): boolean {
        return false;
    }

    protected buildDefaultLink(linksData: LinksConverterData, aEndIdentifier: EndpointElementIdentifier,
            zEndIdentifier: EndpointElementIdentifier): LinkElement[] {
        throw new Error("Diagnostic has no default link");
    }

    private buildInformationBlocks(data: DwdmLinkDiagnosticData): DiagramCardInformation[] {
        const aEnd = this.popoverHelperService.getTextOrPlaceHolder(data.aEnd);
        const zEnd = this.popoverHelperService.getTextOrPlaceHolder(data.zEnd);
        const distance = this.popoverHelperService.getTextOrPlaceHolder(data.distance, KILOMETER);
        const totalAttenuation = this.popoverHelperService.getTextOrPlaceHolder(data.totalAttenuation, DECIBEL);
        const situation = this.translate.instant(LINK_SITUATION[data.situation.trim().toUpperCase()].translationKey);

        return [
            {
                [this.translatedDiagramCardField.ORIGIN_A_END]: aEnd,
                [this.translatedDiagramCardField.DESTINATION_Z_END]: zEnd,
                [this.translatedDiagramCardField.DISTANCE]: distance,
                [this.translatedDiagramCardField.TOTAL_ATTENUATION]: totalAttenuation,
                [this.translatedDiagramCardField.SITUATION]: situation
            }
        ]
    }

    private buildStatus(data: DwdmLinkDiagnosticData) {
        return {
            severity: this.popoverHelperService.normalizeCardSeverity(data.status),
            message: this.severityLabelBySeverity[data.status]
        };
    }

}
